import type { ActivityRow } from "../lib/api";
import { typeLabelKey } from "../lib/activity";
import { t, type Language } from "../lib/i18n";
import { formatRelative } from "../lib/relativeTime";
import { useNow } from "../lib/useNow";
import { CorrelationId } from "./CorrelationId";
import { Icon } from "./icons";

interface ActivityRowItemProps {
  row: ActivityRow;
  language: Language;
}

/** One Atividade / teaser row (§13.2): the friendly sentence + timestamp +
 * a copyable correlation-id chip + the curated `Tipo` tag + an attention
 * marker. Shared by the Overview teaser AND the Atividade tab so the row
 * shape can't drift between the two surfaces.
 *
 * `needs_attention` is the single canonical signal the marker keys on (a
 * failure OR a flagged-but-successful action) — the same source the Estado
 * filter uses, so the row marker and the filter can never disagree. The
 * marker carries a text label (not colour alone) so it survives the
 * colour-blind / monochrome case (WCAG 1.4.1). Universal — no tenant copy. */
export function ActivityRowItem({
  row,
  language,
}: ActivityRowItemProps): JSX.Element {
  const labelKey = typeLabelKey(row.type);
  const now = useNow(30_000);
  const occurredAt = new Date(row.timestamp);
  return (
    <li
      className="flex items-start gap-3 py-2"
      data-testid="activity-row"
    >
      {/* Icon slot (Task 6): the agent narrates its own work — an
       * agent-authored (non-attention) row shows the agent mark; an
       * attention row keeps the loud amber alert-triangle so the
       * distinction stays obvious (and the meta keeps a text marker, so the
       * signal survives monochrome — WCAG 1.4.1).
       *
       * The mark is a square glyph, NOT the avatar photo: at 28px the
       * low-poly artwork was an unreadable blob, so the gutter whose whole
       * job is "agent did this" vs "needs attention" carried no signal
       * (design review finding 3). The art stays where it works — the 42px
       * status strip, where the brand moment belongs. */}
      {row.needs_attention ? (
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] bg-kwanza-warnTint text-kwanza-warnText"
          data-testid="activity-icon-attention"
          aria-hidden="true"
        >
          <Icon name="alert-triangle" size={16} />
        </span>
      ) : (
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] bg-kwanza-accentSoft text-kwanza-accentText"
          data-testid="activity-icon-agent"
          aria-hidden="true"
        >
          <Icon name="check-circle" size={16} />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm text-kwanza-ink">{row.sentence}</span>
        <div className="flex flex-wrap items-center gap-2">
          {/* Relative time, absolute on hover (design review finding 10):
           * `toLocaleString` printed "27/07/2026, 09:12:00" on every row —
           * seconds included, and the same date repeated down the column. */}
          <span
            className="text-xs text-kwanza-inkSoft"
            title={occurredAt.toLocaleString(language)}
            data-testid="activity-time"
          >
            {formatRelative(occurredAt.getTime(), now, language)}
          </span>
          {labelKey ? (
            <span
              className="rounded bg-kwanza-card px-1.5 py-0.5 text-xs text-kwanza-inkSoft ring-1 ring-inset ring-kwanza-divider"
              data-testid="activity-type-tag"
            >
              {t(labelKey, language)}
            </span>
          ) : null}
          {row.needs_attention ? (
            <span
              className="inline-flex items-center gap-1 rounded bg-kwanza-warnTint px-1.5 py-0.5 text-xs font-semibold text-kwanza-warnText"
              data-testid="activity-attention"
            >
              <span aria-hidden="true">⚠</span>
              {t("estado.attention", language)}
            </span>
          ) : null}
        </div>
      </div>
      <CorrelationId value={row.correlation_id} language={language} />
    </li>
  );
}
